import React, { useState, useEffect } from "react";
import Head from "next/head";
import Link from "next/link";
import { useStore } from "jotai";
import classNames from "classnames";
import { isIOS } from "react-device-detect";
import {
  AUTH_STATUS,
  AuthModalAtom,
} from "@models/state/atoms/auth-modal.atom";
import useAmplitude from "@models/interactions/use-amplitude";
import Button from "@components/atoms/button";
import Icon from "@components/atoms/icon";
import DownloadAppMobile from "@components/molecules/download-app-mobile";
import CommonHeader from "@components/organisms/common-header";
import CommonFooter from "@components/organisms/common-footer";
import CommonLayout from "@components/templates/common-layout";
import ListSVG from "@components/atoms/svg/list";
import StarSVG from "@components/atoms/svg/star";

type SignUpButtonProps = {
  onClick: () => void;
};

type PlatformRowProps = {
  Icon: React.ReactNode;
  text: string;
};

type PlatformCardProps = {
  title: string;
  content: string | React.ReactNode;
  image: React.ReactNode;
  reverse?: Boolean;
  signupAction: () => void;
};

type TestimonialUser = {
  displayname: string;
  firstname: string;
  lastname: string;
  photo: string;
};

type TestimonialBoxProps = {
  quote: string;
  user: TestimonialUser;
};

const MATRIX = [
  ["", "X/Twitter", "Substack", "Reddit", "Medium", "Memo’d"],
  ["Noise-free", "No", "Yes", "No", "Yes", "Yes"],
  ["Short-form writing skills", "Yes", "No", "No", "No", "Yes"],
  ["Easy writing template", "Yes", "No", "No", "No", "Yes"],
  ["Organize & create body of work", "Yes", "Yes", "No", "Yes", "Yes"],
  ["Free mentoring", "No", "No", "No", "No", "Yes"],
  [
    "Chance to monetize",
    "Only for a few",
    "Yes",
    "Yes",
    "Only for a few",
    "Yes",
  ],
];

function SignUpButton(props: SignUpButtonProps) {
  return (
    <Button.Pill
      variant="primary"
      className="h-auto !py-3 text-base"
      onClick={props.onClick}
    >
      Join Memo’d
    </Button.Pill>
  );
}

function PlatformRow(props: PlatformRowProps) {
  return (
    <div className="flex items-center gap-6">
      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-memod-separator bg-memod-button text-white">
        {props.Icon}
      </div>
      <h4 className="text-xl leading-8">{props.text}</h4>
    </div>
  );
}

function PlatformCard(props: PlatformCardProps) {
  return (
    <div
      className={classNames(
        "mx-auto flex flex-col items-center gap-12 py-16 xl:max-w-[960px]",
        {
          "md:flex-row": !props.reverse,
          "md:flex-row-reverse": props.reverse,
        }
      )}
    >
      <div className="md:flex md:shrink-0 md:basis-[400px] md:justify-center">
        {props.image}
      </div>
      <div className="flex flex-col gap-10 self-stretch md:grow">
        <div className="flex flex-col gap-2">
          <h3 className="text-[32px] font-bold leading-[1.2]">{props.title}</h3>
          <div className="leading-normal">{props.content}</div>
        </div>
        <div>
          <SignUpButton onClick={props.signupAction} />
        </div>
      </div>
    </div>
  );
}

function TestimonialBox(props: TestimonialBoxProps) {
  const fullname = `${props.user.firstname} ${props.user.lastname}`;

  return (
    <div className="flex h-[318px] flex-col justify-between rounded-xl border border-memod-separator p-6 md:basis-1/2">
      <p>{props.quote}</p>
      <div className="flex items-center gap-2 rounded-lg bg-memod-button px-4 py-6">
        <div>
          <img
            src={props.user.photo}
            alt={fullname}
            className="rounded-full"
            style={{
              width: 60,
              height: 60,
            }}
            loading="lazy"
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-xl font-semibold leading-8">{fullname}</div>
          <div className="text-sm">@{props.user.displayname}</div>
        </div>
      </div>
    </div>
  );
}

function useCreators() {
  const { operations } = useAmplitude();
  const store = useStore();
  const [size, setSize] = useState(0);

  const signupAction = () => {
    operations.send("SignUp Creators", {
      unknown_user_id: operations.getUserId(),
      opened_at: Date.now(),
    });

    store.set(AuthModalAtom, {
      mode: AUTH_STATUS.SIGN_UP,
      open: true,
    });
  };

  function resize() {
    setSize(window.innerWidth);
  }

  useEffect(() => {
    const isClient = typeof window !== "undefined";

    if (isClient) {
      resize();
      window.addEventListener("resize", resize);

      return () => window.removeEventListener("resize", resize);
    }
  }, []);

  return {
    models: {
      size,
    },
    operations: {
      signupAction,
    },
  };
}

export default function Creators() {
  const { models, operations } = useCreators();

  return (
    <CommonLayout
      Header={
        <div className="fixed z-50 w-full">
          <DownloadAppMobile
            className={classNames({
              "md:hidden": isIOS,
              hidden: !isIOS,
            })}
          />
          <CommonHeader
            search
            hiddeBack
            showHomeLinks
            autocomplete
            className="bg-composer-bg"
          />
        </div>
      }
      Footer={<CommonFooter />}
      className={classNames(
        "!items-stretch bg-composer-bg !px-0 xl:mx-auto xl:max-w-[1440px] xl:flex-col",
        {
          "!pt-[132px]": isIOS && models.size < 720,
        }
      )}
    >
      <Head>
        <link rel="canonical" href="https://memod.com/creators" />
      </Head>
      <div className="flex flex-col text-memod-white-base">
        {/* HERO SECTION */}
        <div className="relative overflow-hidden bg-memod-button px-14 pb-18 pt-24 md:py-24">
          <div className="relative z-10 mx-auto flex flex-col items-center gap-6 xl:max-w-[896px]">
            <h1 className="text-center text-5xl font-semibold md:text-[64px]">
              A platform where curiosity meets wisdom
            </h1>
            <h4 className="text-xl leading-8 md:text-2xl">
              …and concise ideas shine
            </h4>
            <SignUpButton onClick={operations.signupAction} />
          </div>
          <StarSVG className="absolute bottom-[50px] left-[65%] z-0 hidden md:block" />
          <div className="absolute left-[18%] top-[39%] z-0 hidden h-[83px] w-[67px] md:block">
            <div className="relative h-full w-full">
              <StarSVG size={53} className="absolute right-0 top-0" />
              <StarSVG size={27} className="absolute bottom-0 left-0" />
            </div>
          </div>
          {/* MOBILE LIST SVG */}
          <div className="absolute -bottom-[90px] -right-[100px] z-0 h-[310px] w-[310px] -rotate-[19deg] md:hidden">
            <ListSVG size={310} strokeWidth={1} />
          </div>
          {/* TABLES LIST SVG */}
          <div className="absolute -bottom-[120px] left-[45%] z-0 hidden h-[310px] w-[310px] -translate-x-1/2 -rotate-[19deg] md:block xl:hidden">
            <ListSVG size={310} strokeWidth={1} />
          </div>
          {/* DESKTOP LIST SVG */}
          <div className="absolute -bottom-[90px] left-[29%] z-0 hidden h-[310px] w-[310px] -translate-x-1/2 -rotate-[19deg] xl:block">
            <ListSVG size={310} strokeWidth={1} />
          </div>
          <div className="absolute bottom-[80px] right-[12%] z-0 hidden h-[186px] w-[186px] -translate-x-1/2 rotate-[19deg] xl:block">
            <ListSVG size={186} strokeWidth={1} />
          </div>
        </div>
        {/* PLATFORM INTRO */}
        <div className="px-9 py-16">
          <div className="mx-auto flex flex-col gap-10 md:flex-row xl:max-w-[960px]">
            <h2 className="my-6 text-[32px] font-semibold">
              Memo’d is the ultimate free platform to:
            </h2>
            <div className="flex flex-col gap-4">
              <PlatformRow
                Icon={<Icon.List size={72} />}
                text="Publish short pieces of content in a noise-free environment"
              />
              <PlatformRow
                Icon={<Icon.Search size={64} />}
                text="Showcase your short-form work in one SEO-friendly place"
              />
              <PlatformRow
                Icon={
                  <div className="relative">
                    <Icon.TipBox size={60} />
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/3 text-2xl">
                      $
                    </span>
                  </div>
                }
                text="Make money from your ideas + receive free mentorship"
              />
            </div>
          </div>
        </div>
        {/* PLATFORMS SECTION */}
        <div className="px-9">
          <PlatformCard
            title="Get discovered"
            content="Don’t let good ideas go to waste. Give your short-form writing the attention it deserves."
            image={
              <img
                src="/img/creators/get-discovered.png"
                alt="Get Discovered"
                style={{
                  width: 394,
                  height: 237,
                }}
                loading="lazy"
              />
            }
            signupAction={operations.signupAction}
          />
          <PlatformCard
            title="Connect"
            content="Create a shareable body of work for an audience that’s hungry for wisdom."
            image={
              <img
                src="/img/creators/connect.jpg"
                alt="Connect"
                style={{
                  width: 400,
                  height: 254,
                }}
                loading="lazy"
              />
            }
            reverse={true}
            signupAction={operations.signupAction}
          />
          <PlatformCard
            title="Grow"
            content="Get 1:1 help from successful writers on what it takes to write viral content."
            image={
              <img
                src="/img/creators/grow.jpg"
                alt="Connect"
                style={{
                  width: 317,
                  height: 228,
                }}
                loading="lazy"
              />
            }
            signupAction={operations.signupAction}
          />
          <PlatformCard
            title="Earn"
            content={
              <>
                <span>Monetize your work by:</span>
                <ul className="list-disc pl-5">
                  <li>receiving tips from readers</li>
                  <li>“locking” content at a price of your choosing</li>
                  <li>qualifying for creator pool payouts</li>
                  <li>enjoying zero subscription or tier costs</li>
                </ul>
              </>
            }
            image={
              <img
                src="/img/creators/earn.jpg"
                alt="Connect"
                style={{
                  width: 239,
                  height: 186,
                }}
                loading="lazy"
              />
            }
            reverse={true}
            signupAction={operations.signupAction}
          />
        </div>
        {/* TESTIMONIALS SECTION */}
        <div className="px-9 py-16">
          <div className="mx-auto flex flex-col xl:max-w-[960px]">
            <h4 className="mb-10 text-center text-[32px] font-bold leading-[1.2]">
              Testimonials
            </h4>
            <div className="flex flex-col gap-8 md:flex-row">
              <TestimonialBox
                quote="
                  “Tweets get lost and articles are too inaccessible.
                  The only writing that will continue to resonate in a few years will be bite-sized, value-driven,
                  and respectful of the readers’ time - everything that Memo'd stands for.”
                "
                user={{
                  firstname: "Jash",
                  lastname: "Dholani",
                  displayname: "jashdholani",
                  photo: "https://cdn.memod.com/images/7bKvmQ.jpg",
                }}
              />
              <TestimonialBox
                quote="
                  “Memo’d has enabled me to improve my writing skills, come up with interesting and creative ideas,
                  and write them in a concise manner. It has a lot to offer for readers and writers alike.
                  Seamless interface and just great content to improve our lives.”
                "
                user={{
                  firstname: "Chirag",
                  lastname: "Malik",
                  displayname: "BooksMyRefuge",
                  photo: "https://cdn.memod.com/images/DCmcCr.jpg",
                }}
              />
            </div>
          </div>
        </div>
        {/* COMPARISON MATRIX SECTION */}
        <div className="hidden px-6 py-16 md:block">
          <div
            className="mx-auto grid grid-cols-[241px_repeat(5,minmax(0,1fr))] text-xl
            leading-normal xl:max-w-[940px]
            [&>div:nth-child(6n)]:border-r-0
            [&>div:nth-child(6n+12)]:bg-[#34504d]
            [&>div:nth-child(n+37)]:border-b-0"
          >
            {MATRIX.map((row) =>
              row.map((column) => (
                <div
                  className={classNames(
                    "border-b border-r border-memod-separator p-3",
                    {
                      "text-base": column.includes("Only"),
                    }
                  )}
                >
                  {column}
                </div>
              ))
            )}
          </div>
        </div>
        {/* QUESTION SECTION */}
        <div className="px-9 py-16">
          <Link href="/help">
            <h5 className="text-center text-[32px] leading-[1.2]">
              <span className="block font-semibold md:mr-1 md:inline">
                Questions?
              </span>
              <span>Please see our FAQ</span>
            </h5>
          </Link>
        </div>
      </div>
    </CommonLayout>
  );
}
